import axios from 'axios';

export interface TaskFormInfo {
  key: string | null;
  camundaFormRef: string | null;
  contextPath: string | null;
}

export interface DesignerFormResponse {
  id: string;
  nameSlug: string;
  version: string;
  formioJson: string;
  formioJsonContentType: string;
  form: {
    createdBy: string | null;
    createdDate: string;
    lastModifiedBy: string;
    lastModifiedDate: string;
    orgId: string | null;
    id: string;
    name: string;
    nameSlug: string;
    description: string;
    version: string;
    publicCode: string | null;
    processKey: string | null;
  };
}

// Get auth token from localStorage
const getAuthToken = (): string => {
  try {
    const tokenData = localStorage.getItem('oidc.user:https://portal-eroyal.pmdgtech.com/auth/realms/doit:web_app');
    if (tokenData) {
      const parsedData = JSON.parse(tokenData);
      return parsedData.access_token || '';
    }
    return '';
  } catch (error) {
    console.error('Error retrieving access token:', error);
    return '';
  }
};

// Create API instance for Camunda
const camundaApi = axios.create({
  baseURL: import.meta.env.MODE === 'development' ? '/engine-rest' : '/engine/engine-rest',
});

// Add request interceptor for auth token
camundaApi.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Create API instance for Designer
const designerApi = axios.create({
  baseURL: import.meta.env.MODE === 'development' ? '/designer/' : '/designer/',
});

// Add request interceptor for auth token
designerApi.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Get task form info from Camunda
export const getTaskFormInfo = async (taskId: string): Promise<TaskFormInfo> => {
  const response = await camundaApi.get<TaskFormInfo>(`/task/${taskId}/form`);
  return response.data;
};

// Get form content from Designer API
export const getDesignerFormContent = async (formKey: string): Promise<DesignerFormResponse> => {
  try {
    const response = await designerApi.get<DesignerFormResponse>(`/${formKey}`);
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 500 && error.response?.data?.detail?.includes('formContent" is null')) {
      throw new Error(`The form layout has not been saved yet for '${formKey.split('/').pop()}'. Please edit and save the form in the Form Builder before using it.`);
    }
    throw error;
  }
};

// Parse and decode Formio JSON from Designer response
export const parseFormioJson = (formioJsonBase64: string): any => {
  const decodedJson = atob(formioJsonBase64);
  return JSON.parse(decodedJson);
};

// Export all functions as default object
export default {
  getTaskFormInfo,
  getDesignerFormContent,
  parseFormioJson,
};
